/**
 * reportError — the one place every caught error should flow through.
 *
 * Design goals:
 * - Forwards every error to Sentry via the @sentry/react SDK initialised in
 *   src/main.tsx. The SDK is configured once at app start; this helper just
 *   adds a consistent scope tag and structured extras to every report.
 * - Always logs in dev (with a [reportError] prefix) so the developer sees
 *   the same trail Sentry sees.
 * - Never throws. An error in the error path is the worst possible bug.
 * - Stable scope strings so searches across Sentry issues are reliable.
 *
 * Usage:
 *   try { ... } catch (err) { reportError('pendingSave.resume', err, { userId }); }
 *
 * Scope convention:
 *   '<feature>.<action>' lowercase dotted, no spaces.
 *   Examples: 'pendingSave.resume', 'geolocation.reverseGeocode',
 *             'llm.ranking', 'cache.read', 'subscription.refresh'.
 */

import * as Sentry from '@sentry/react';

type ReportExtras = Record<string, unknown>;

interface ReportOptions {
  level?: Sentry.SeverityLevel;
  /** Overrides Sentry's grouping so one cause is one issue with a count. */
  fingerprint?: string[];
}

const isDev = (() => {
  try {
    return Boolean((import.meta as ImportMeta & { env?: { DEV?: boolean } }).env?.DEV);
  } catch {
    return false;
  }
})();

export function reportError(scope: string, err: unknown, extras?: ReportExtras, options?: ReportOptions): void {
  try {
    const message = err instanceof Error ? err.message : String(err);
    const stack = err instanceof Error ? err.stack : undefined;

    // Always log in dev with a consistent shape.
    if (isDev) {
      console.error(`[reportError] ${scope}: ${message}`, {
        stack,
        extras,
      });
    }

    // Forward to Sentry. The SDK is initialised in src/main.tsx; if for any
    // reason init didn't run (e.g. very early failure before main.tsx loaded)
    // captureException is a safe no-op.
    Sentry.captureException(err, {
      tags: { scope },
      extra: extras ?? {},
      ...(options?.level && { level: options.level }),
      ...(options?.fingerprint && { fingerprint: options.fingerprint }),
    });

    // In prod, also drop a breadcrumb to the browser console so a debugger
    // session can find it without round-tripping to Sentry.
    if (!isDev) {
      console.error(`[reportError] ${scope}: ${message}`);
    }
  } catch {
    // Intentionally swallow. An error in the error reporter must never crash
    // the app. This is the only `catch {}` permitted in the codebase.
  }
}
